import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react'
import { Form, Row, Col, Card, Spin, Tabs, Tooltip, Space } from 'antd'
import Button from '../../../../core/components/Button.jsx'
import { RecordFormDeleteEllipses } from '../../../../core/components/RecordFormDeleteEllipses.tsx'
import { AgentProvenanceTag } from '../../../../core/components/AgentProvenanceTag.tsx'
import { RecordProvenanceTag } from '../../../../core/components/RecordProvenanceTag.tsx'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { useSelector, useDispatch } from 'react-redux'
import { DndProvider } from 'react-dnd'
import { HTML5Backend } from 'react-dnd-html5-backend'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faArrowsRotate } from '@fortawesome/free-solid-svg-icons'
import { toast } from 'react-toastify'
import { showErrorToast } from '../../../../core/utils/errorToast.tsx'
import { fetchDealById, deleteDeal } from '../../services/dealsService.ts'
import { updateDealRecord, removeDealFromList } from '../../../../state_manager/state_update_actions/deals/deal_actions.ts'
import { getAccessToken } from '../../../../core/auth/authService'
import { store } from '../../../../state_manager/store.ts'
import { removeTab, selectActiveTabKey, updateTabPath } from '../../../../store/slices/tabSlice'
import { stripTrailingFormRouteSegments } from '../../../../core/contexts/DrawerContext.jsx'
import { selectDealById } from '../../../../state_manager/state_update_actions/deals/selectors.ts'
import {
  setActiveRecord,
  registerRecordUsage,
  unregisterRecordUsage,
  removeActiveRecord
} from '../../../../state_manager/state_update_actions/active_records/active_record_actions.ts'
import { selectActiveRecordById } from '../../../../state_manager/state_update_actions/active_records/selectors.ts'
import { DashboardTab } from './tabs/DashboardTab.tsx'
import { DetailTab } from './tabs/DetailTab.tsx'
import { ChangeLogTab } from '../../../change_log/components/ChangeLogTab.tsx'
import ExecutionTab from '../../../deal_execution/components/ExecutionTab.tsx'
import { AgentTab } from '../../../internal_agents/components/AgentTab.tsx'
import WireTab from '../../../deal_execution/components/WireTab.tsx'
import DocumentsTab from '../../../deal_execution/components/DocumentsTab.tsx'
import { DealFields } from '../../../../state_manager/data_models/deal_record.ts'
import { DealCommitmentEntryFields } from '../../../../state_manager/data_models/deal_commitment_entry.ts'
import { CompanyTeamEntryFields } from '../../../../state_manager/data_models/company_team_entry.ts'
import { RELATIONSHIP_TYPE_EMPLOYEE, RELATIONSHIP_CATEGORY_FOUNDER } from '../../../../state_manager/globals.ts'
import { getCurrencySymbol } from '../../../../state_manager/constants/currencies.ts'
import {
  UNKNOWN_CONTACT,
  CONFIRM_DELETE_DEAL_TITLE
} from '../../../../state_manager/constants/strings.ts'

interface FullDealFormProps {
  recordId?: string | null
  isNew?: boolean
}

export default function FullDealForm({ recordId: propRecordId, isNew: propIsNew }: FullDealFormProps) {
  const { urlRecordId } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const dispatch = useDispatch()

  const recordId = propRecordId !== undefined ? propRecordId : urlRecordId
  const isNew = propIsNew !== undefined ? propIsNew : recordId === 'new'

  const fundOptions = useSelector((state: any) => state.globalConfigs.funds || [])
  const countriesData = useSelector((state: any) => state.globalConfigs?.countries || [])
  const dealsState = useSelector((state: any) => state.deals)

  const dealFromActiveRecords = useSelector((state: any) =>
    recordId && !isNew ? selectActiveRecordById(state, recordId) : null
  )
  const dealFromState = useSelector((state: any) => (recordId && !isNew ? selectDealById(state, recordId) : null))
  const currentDeal = dealFromActiveRecords || dealFromState

  const [form] = Form.useForm()

  const [dealTeam, setDealTeam] = useState<any[]>([])
  const [commitments, setCommitments] = useState<any[]>([])

  const [companyTeamMembers, setCompanyTeamMembers] = useState<any[]>([])

  const [isFetchingDeal, setIsFetchingDeal] = useState<boolean>(false)
  const [isDeletingDeal, setIsDeletingDeal] = useState<boolean>(false)
  const activeTabKey = useSelector((state: any) => selectActiveTabKey(state))
  const hasAttemptedFetch = useRef<boolean>(false)
  const dealsStateRef = useRef<any>(dealsState)
  const refreshInteractionsRef = useRef<(() => Promise<void>) | null>(null)
  const refreshTasksRef = useRef<(() => Promise<void>) | null>(null)
  // The deal's files live on the Documents tab (the Dashboard Files card was
  // removed 2026-09-13). Tabs never unmount, so without this the Documents
  // list would stay on whatever it fetched when first opened.
  const refreshDocumentsRef = useRef<(() => Promise<void>) | null>(null)
  const refreshAgentTabRef = useRef<(() => Promise<void>) | null>(null)
  const refreshAgentSignalsRef = useRef<(() => Promise<void>) | null>(null)
  const refreshDealStatesRef = useRef<(() => Promise<void>) | null>(null)
  // Execution-tab agent strip (execution follower states + signals).
  const refreshExecutionAgentRef = useRef<(() => Promise<void>) | null>(null)
  // Execution tab DATA (row + context + legal terms) and Wire tab loaders —
  // previously missing from the Refresh fan-out, so a transaction booked in
  // another tab never appeared on those tabs until a hard refresh.
  const refreshExecutionDataRef = useRef<(() => Promise<void>) | null>(null)
  const refreshWireRef = useRef<(() => Promise<void>) | null>(null)

  const companyTeamDataKey = useMemo(() => {
    const team = currentDeal?.[DealFields.prog__company_team]
    if (!team || !Array.isArray(team)) {
      return team === undefined ? undefined : '[]'
    }
    if (team.length === 0) {
      return '[]'
    }
    return JSON.stringify(
      team.map((m: any) => ({
        id: m[CompanyTeamEntryFields.contact_accounts__id],
        contactId: m[CompanyTeamEntryFields.contacts__id],
        founder: m[CompanyTeamEntryFields.prog__founder],
        current: m[CompanyTeamEntryFields.contact_accounts__current]
      }))
    )
  }, [currentDeal?.[DealFields.prog__company_team]])

  const companyTeamMembersKey = useMemo(() => {
    if (!companyTeamMembers || !Array.isArray(companyTeamMembers) || companyTeamMembers.length === 0) {
      return companyTeamMembers === undefined ? undefined : '[]'
    }
    return JSON.stringify(
      companyTeamMembers.map((m: any) => ({
        key: m.key,
        founder: m.founder,
        contactId: m.contact?.value,
        category: m.category
      }))
    )
  }, [companyTeamMembers])

  // Pre-selection for tasks raised from the Execution tab's agent strip: the
  // deal's lead is the person they would normally fall to (the Dashboard has
  // its own copy of this memo). Only a default — the picker stays editable.
  const dealLeadAssignees = useMemo(() => {
    const leads = (currentDeal?.[DealFields.contact_deals__lead] ?? []) as any[]
    return (Array.isArray(leads) ? leads : [])
      .filter((lead) => lead?.contacts__id)
      .map((lead) => ({
        value: lead.contacts__id,
        label: lead.prog__display_name || '',
        email: lead.contacts__email || null
      }))
  }, [currentDeal])

  const handleRefreshTasksPanel = useCallback(async () => {
    if (refreshTasksRef.current) {
      await refreshTasksRef.current()
    }
  }, [])

  const dashboardTabKey = `dashboard-${recordId || 'new'}-${isNew ? 'new' : 'edit'}`
  const detailTabKey = `detail-${recordId || 'new'}-${isNew ? 'new' : 'edit'}`
  const executionTabKey = `execution-${recordId || 'new'}-${isNew ? 'new' : 'edit'}`
  const wireTabKey = `wire-${recordId || 'new'}-${isNew ? 'new' : 'edit'}`
  const documentsTabKey = `documents-${recordId || 'new'}-${isNew ? 'new' : 'edit'}`
  const agentTabKey = `agent-${recordId || 'new'}-${isNew ? 'new' : 'edit'}`
  // Deal-follower surfaces (Agent tab + the Dashboard signals/brief panels)
  // show while the deal is — or has been — in the Origination or Execution
  // pipeline. The denormalized pipeline pointers persist after the deal
  // leaves, so the follower's history stays reachable through the unfollow
  // lag and after the sweep pauses it.
  const hasDealFollower = Boolean(
    !isNew &&
      recordId &&
      (currentDeal?.[DealFields.deals__pipeline_origination] ||
        currentDeal?.[DealFields.deals__pipeline_execution])
  )
  const changeLogTabKey = `change-log-${recordId || 'new'}-${isNew ? 'new' : 'edit'}`
  // Deep-link tab target, read from the query string set by openFormInTab
  // callers (e.g. the stage-alerts bell and digest links use ?tab=execution).
  const resolveTabFromQuery = () => {
    if (isNew) return dashboardTabKey
    const params = new URLSearchParams(window.location.search)
    switch (params.get('tab')) {
      case 'execution':
        return executionTabKey
      case 'wire':
        return wireTabKey
      case 'documents':
        return documentsTabKey
      case 'agent':
        return agentTabKey
      default:
        return dashboardTabKey
    }
  }
  const [activeTab, setActiveTab] = useState<string>(resolveTabFromQuery)

  useEffect(() => {
    setActiveTab(resolveTabFromQuery())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dashboardTabKey])

  // Re-resolve the deep-link target when the query string changes while this
  // form is already mounted (e.g. execution-board or alerts-bell link to a
  // deal whose form tab is already open). Guards: only when the URL actually
  // points at this record (inactive form tabs stay mounted with display:none,
  // and the global location belongs to the active tab) and only when a ?tab=
  // param is present — its absence just means the deep link was consumed.
  useEffect(() => {
    if (isNew || !recordId) return
    if (!location.pathname.endsWith(`/forms/full/${recordId}`)) return
    if (!new URLSearchParams(location.search).get('tab')) return
    setActiveTab(resolveTabFromQuery())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, location.search])

  const handlePatchSuccess = useCallback(
    (patchRecordId: string, updates: Record<string, any>) => {
      if (!patchRecordId) return

      if (DealFields.accounts__business_country_code in updates) {
        const countryCode = updates[DealFields.accounts__business_country_code]
        if (countryCode && countriesData.length > 0) {
          updates[DealFields.accounts__business_country_label] = countriesData.find(
            (country: any) => country.code === countryCode
          )?.name
        } else {
          updates[DealFields.accounts__business_country_label] = null
        }
      }

      if (DealFields.accounts__office_country_code in updates) {
        const officeCountryCode = updates[DealFields.accounts__office_country_code]
        if (officeCountryCode && countriesData.length > 0) {
          updates[DealFields.accounts__office_country_code__label] = countriesData.find(
            (country: any) => country.code === officeCountryCode
          )?.name
        } else {
          updates[DealFields.accounts__office_country_code__label] = null
        }
      }

      dispatch(updateDealRecord({ dealId: patchRecordId, updates }))
    },
    [dispatch, countriesData]
  )

  useEffect(() => {
    dealsStateRef.current = dealsState
  }, [dealsState])

  useEffect(() => {
    hasAttemptedFetch.current = false
  }, [recordId, dispatch])

  const previousRecordIdRef = useRef<string | null | undefined>(null)

  useEffect(() => {
    const recordIdString = recordId?.toString()
    const previousRecordIdString = previousRecordIdRef.current?.toString()

    if (!isNew && recordIdString && recordIdString !== previousRecordIdString) {
      const currentDealNow = dealFromActiveRecords || dealFromState

      if (currentDealNow) {
        hasAttemptedFetch.current = true
        dispatch(setActiveRecord({ uuid: recordIdString, record: currentDealNow }))
        dispatch(registerRecordUsage({ uuid: recordIdString, context: 'fullForm' }))
      } else if (!hasAttemptedFetch.current) {
        const currentToken = getAccessToken(store.getState().auth)
        if (currentToken) {
          hasAttemptedFetch.current = true
          setIsFetchingDeal(true)
          fetchDealById(recordIdString, currentToken)
            .then((response: any) => {
              if (response.data && !response.error) {
                const fetchedDeal = response.data
                dispatch(setActiveRecord({ uuid: recordIdString, record: fetchedDeal }))
                dispatch(registerRecordUsage({ uuid: recordIdString, context: 'fullForm' }))
              } else if (response.error) {
                console.error('Error fetching deal:', response.error)
              }
            })
            .catch((error: any) => {
              console.error('Error fetching deal:', error)
            })
            .finally(() => {
              setIsFetchingDeal(false)
            })
        }
      }
      previousRecordIdRef.current = recordId
    }
  }, [recordId, isNew, dispatch, dealFromActiveRecords, dealFromState])

  useEffect(() => {
    return () => {
      if (recordId && !isNew) {
        const dealIdString = recordId.toString()
        dispatch(unregisterRecordUsage({ uuid: dealIdString, context: 'fullForm' }))
      }
    }
  }, [recordId, isNew, dispatch])

  const handleDeleteDeal = useCallback(async () => {
    if (!recordId || isNew) return
    const token = getAccessToken(store.getState().auth)
    if (!token) {
      showErrorToast('Not authenticated')
      return
    }
    setIsDeletingDeal(true)
    try {
      await deleteDeal(recordId, token)
      toast.success('Record deleted')
      const id = String(recordId)
      dispatch(removeDealFromList({ dealId: id }))
      dispatch(removeActiveRecord({ uuid: id }))
      dispatch(unregisterRecordUsage({ uuid: id, context: 'fullForm' }))
      window.isClosingTab = true
      dispatch(removeTab({ key: activeTabKey }))
      const nextState = store.getState().tabs
      const nextTab = nextState.tabs.find((t: { key: string }) => t.key === nextState.activeTabKey)
      if (nextTab?.path) {
        const pathWithoutFormShell = stripTrailingFormRouteSegments(nextTab.path)
        if (pathWithoutFormShell !== nextTab.path) {
          dispatch(updateTabPath({ key: nextTab.key, path: pathWithoutFormShell }))
        }
        navigate(pathWithoutFormShell, { replace: true })
      }
      setTimeout(() => {
        window.isClosingTab = false
      }, 150)
    } catch (error) {
      showErrorToast(error instanceof Error ? error.message : 'Failed to delete deal')
    } finally {
      setIsDeletingDeal(false)
    }
  }, [recordId, isNew, dispatch, navigate, activeTabKey])

  const handleRefresh = useCallback(async () => {
    if (!recordId || isNew) {
      return
    }

    const currentToken = getAccessToken(store.getState().auth)
    if (!currentToken) {
      return
    }

    setIsFetchingDeal(true)
    try {
      const result = await fetchDealById(recordId, currentToken)
      if (result && result.data) {
        const recordIdString = recordId.toString()
        dispatch(setActiveRecord({ uuid: recordIdString, record: result.data }))
        dispatch(registerRecordUsage({ uuid: recordIdString, context: 'fullForm' }))
        dispatch(updateDealRecord({ dealId: recordIdString, updates: result.data }))
        toast.success('Refreshed Successfully')

        if (refreshInteractionsRef.current) {
          await refreshInteractionsRef.current()
        }

        if (refreshTasksRef.current) {
          await refreshTasksRef.current()
        }

        if (refreshDocumentsRef.current) {
          await refreshDocumentsRef.current()
        }

        if (refreshAgentTabRef.current) {
          await refreshAgentTabRef.current()
        }

        if (refreshAgentSignalsRef.current) {
          await refreshAgentSignalsRef.current()
        }

        if (refreshDealStatesRef.current) {
          await refreshDealStatesRef.current()
        }

        if (refreshExecutionAgentRef.current) {
          await refreshExecutionAgentRef.current()
        }

        if (refreshExecutionDataRef.current) {
          await refreshExecutionDataRef.current()
        }

        if (refreshWireRef.current) {
          await refreshWireRef.current()
        }
      }
    } catch (error) {
      console.error('Error refreshing deal:', error)
    } finally {
      setIsFetchingDeal(false)
    }
  }, [recordId, isNew, dispatch])

  useEffect(() => {
    if (currentDeal && !isNew && recordId && countriesData.length > 0) {
      const countryCode = currentDeal[DealFields.accounts__business_country_code]
      const countryLabel = currentDeal[DealFields.accounts__business_country_label]

      if (countryCode && !countryLabel) {
        const foundCountry = countriesData.find((country: any) => country.code === countryCode)
        if (foundCountry) {
          dispatch(
            updateDealRecord({
              dealId: recordId,
              updates: {
                [DealFields.accounts__business_country_label]: foundCountry.name
              }
            })
          )
        }
      }
    }
  }, [currentDeal, countriesData, recordId, isNew, dispatch])

  useEffect(() => {
    if (currentDeal && !isNew) {
      const companyTeam = currentDeal[DealFields.prog__company_team]
      if (companyTeam && Array.isArray(companyTeam) && companyTeam.length > 0) {
        const contactRelationships: Record<string, any> = {}

        companyTeam.forEach((member: any, index: number) => {
          const contactId = member[CompanyTeamEntryFields.contacts__id]
          const isFounder = member[CompanyTeamEntryFields.prog__founder] || false
          const relationshipId = member[CompanyTeamEntryFields.contact_accounts__id]

          const contactData = {
            display_name: member[CompanyTeamEntryFields.prog__display_name],
            first_name: member[CompanyTeamEntryFields.contacts__first_name],
            last_name: member[CompanyTeamEntryFields.contacts__last_name],
            email: member[CompanyTeamEntryFields.contacts__email],
            mobile_phone: member[CompanyTeamEntryFields.contacts__mobile_phone],
            preferred_name: member[CompanyTeamEntryFields.contacts__preferred_name]
          }

          if (!contactRelationships[contactId]) {
            const relationshipCategoryId = member[CompanyTeamEntryFields.contact_accounts__relationship_category_id]
            const category =
              relationshipCategoryId && relationshipCategoryId !== RELATIONSHIP_CATEGORY_FOUNDER
                ? relationshipCategoryId
                : null

            contactRelationships[contactId] = {
              key: relationshipId || `team_${contactId}`,
              employeeRelationshipId: !isFounder ? relationshipId : null,
              founderRelationshipId: isFounder ? relationshipId : null,
              contact: {
                value: contactId,
                label:
                  contactData.display_name ||
                  `${contactData.first_name || ''} ${contactData.last_name || ''}`.trim() ||
                  contactData.email ||
                  UNKNOWN_CONTACT,
                data: contactData
              },
              founder: isFounder,
              category: category,
              jobTitle: member[CompanyTeamEntryFields.contact_accounts__title] || '',
              email: contactData.email || '',
              contactNumber: contactData.mobile_phone || ''
            }
          } else {
            if (!isFounder) {
              contactRelationships[contactId].key = relationshipId
              contactRelationships[contactId].employeeRelationshipId = relationshipId
              const relationshipCategoryId = member[CompanyTeamEntryFields.contact_accounts__relationship_category_id]
              contactRelationships[contactId].category =
                relationshipCategoryId && relationshipCategoryId !== RELATIONSHIP_CATEGORY_FOUNDER
                  ? relationshipCategoryId
                  : null
              contactRelationships[contactId].jobTitle = member[CompanyTeamEntryFields.contact_accounts__title] || ''
            } else {
              contactRelationships[contactId].founderRelationshipId = relationshipId
              contactRelationships[contactId].founder = true
              const relationshipCategoryId = member[CompanyTeamEntryFields.contact_accounts__relationship_category_id]
              if (relationshipCategoryId && relationshipCategoryId !== RELATIONSHIP_CATEGORY_FOUNDER) {
                contactRelationships[contactId].category = relationshipCategoryId
              } else if (!contactRelationships[contactId].category) {
                contactRelationships[contactId].category = null
              }
            }
          }
        })

        const teamMembers = Object.values(contactRelationships)

        const filteredTeamMembers = teamMembers.filter((member: any) => {
          return member.employeeRelationshipId !== null && member.employeeRelationshipId !== undefined
        })

        setCompanyTeamMembers(filteredTeamMembers)
      } else {
        setCompanyTeamMembers([])
      }

      const newDealTeam: any[] = []

      if (
        currentDeal[DealFields.contact_accounts__introduced_by] &&
        Array.isArray(currentDeal[DealFields.contact_accounts__introduced_by])
      ) {
        currentDeal[DealFields.contact_accounts__introduced_by].forEach((contact: any, index: number) => {
          const displayName = contact.preferred_name || contact.first_name || ''
          const fullName = contact.last_name ? `${displayName} ${contact.last_name}`.trim() : displayName
          newDealTeam.push({
            id: `intro_${contact.id}_${index}`,
            contactId: contact.id,
            role: 'introduced_by',
            name: fullName || contact.email || UNKNOWN_CONTACT,
            title: '',
            email: contact.email || '',
            avatar: null,
            initials: (contact.first_name?.charAt(0) || '') + (contact.last_name?.charAt(0) || ''),
            dateAdded: contact.relationship_from || currentDeal.date_created || new Date().toISOString(),
            userType: 'contact'
          })
        })
      }

      if (
        currentDeal[DealFields.contact_accounts__sourced_by] &&
        Array.isArray(currentDeal[DealFields.contact_accounts__sourced_by])
      ) {
        currentDeal[DealFields.contact_accounts__sourced_by].forEach((contact: any, index: number) => {
          const displayName = contact.preferred_name || contact.first_name || ''
          const fullName = contact.last_name ? `${displayName} ${contact.last_name}`.trim() : displayName
          newDealTeam.push({
            id: `sourced_${contact.id}_${index}`,
            contactId: contact.id,
            role: 'sourced_by',
            name: fullName || contact.email || UNKNOWN_CONTACT,
            title: '',
            email: contact.email || '',
            avatar: null,
            initials: (contact.first_name?.charAt(0) || '') + (contact.last_name?.charAt(0) || ''),
            dateAdded: contact.relationship_from || currentDeal.date_created || new Date().toISOString(),
            userType: 'contact'
          })
        })
      }

      if (currentDeal[DealFields.contact_deals__lead] && Array.isArray(currentDeal[DealFields.contact_deals__lead])) {
        currentDeal[DealFields.contact_deals__lead].forEach((contact: any, index: number) => {
          const displayName = contact.preferred_name || contact.first_name || ''
          const fullName = contact.last_name ? `${displayName} ${contact.last_name}`.trim() : displayName
          newDealTeam.push({
            id: `lead_${contact.id}_${index}`,
            contactId: contact.id,
            role: 'lead',
            name: fullName || contact.email || UNKNOWN_CONTACT,
            title: '',
            email: contact.email || '',
            avatar: null,
            initials: (contact.first_name?.charAt(0) || '') + (contact.last_name?.charAt(0) || ''),
            dateAdded: contact.relationship_from || currentDeal.date_created || new Date().toISOString(),
            userType: 'contact'
          })
        })
      }

      if (currentDeal[DealFields.prog__company_team] && Array.isArray(currentDeal[DealFields.prog__company_team])) {
        currentDeal[DealFields.prog__company_team].forEach((member: any, index: number) => {
          const displayName =
            member[CompanyTeamEntryFields.contacts__preferred_name] ||
            member[CompanyTeamEntryFields.contacts__first_name] ||
            ''
          const fullName = member[CompanyTeamEntryFields.contacts__last_name]
            ? `${displayName} ${member[CompanyTeamEntryFields.contacts__last_name]}`.trim()
            : displayName
          const role = member[CompanyTeamEntryFields.prog__founder] ? 'founder' : 'employee'
          const title = member[CompanyTeamEntryFields.contact_accounts__title] || ''

          newDealTeam.push({
            id: `company_team_${member[CompanyTeamEntryFields.contact_accounts__id]}_${index}`,
            contactId: member[CompanyTeamEntryFields.contacts__id],
            role: role,
            name: fullName || member[CompanyTeamEntryFields.contacts__email] || UNKNOWN_CONTACT,
            title: title,
            email: member[CompanyTeamEntryFields.contacts__email] || '',
            avatar: null,
            initials:
              (member[CompanyTeamEntryFields.contacts__first_name]?.charAt(0) || '') +
              (member[CompanyTeamEntryFields.contacts__last_name]?.charAt(0) || ''),
            dateAdded: currentDeal.date_created || new Date().toISOString(),
            userType: 'contact',
            isFounder: member[CompanyTeamEntryFields.prog__founder]
          })
        })
      }

      setDealTeam(newDealTeam)

      const dealCommitmentsData = currentDeal[DealFields.prog__deal_commitments] || []

      if (dealCommitmentsData && dealCommitmentsData.length > 0) {
        const dealCommitments = dealCommitmentsData
          .filter((commitment: any) => {
            const amount = commitment[DealCommitmentEntryFields.deal_commitments__amount]
            return amount !== null && amount !== undefined && amount > 0
          })
          .map((commitment: any, index: number) => {
            const fundId = commitment[DealCommitmentEntryFields.deal_commitments__fund_id]
            const amount = commitment[DealCommitmentEntryFields.deal_commitments__amount]
            const commitmentId = commitment[DealCommitmentEntryFields.deal_commitments__id]
            const fundOption = fundOptions?.find((option: any) => option.id === fundId)
            return {
              key: commitmentId || `commitment_${index}`,
              fund: fundId,
              fundId: fundId,
              fundName: fundId ? fundOption?.name || `Fund ID: ${fundId}` : 'No Fund Selected',
              amount: amount || 0
            }
          })
        setCommitments(dealCommitments)
      } else {
        setCommitments([])
      }
    } else if (isNew) {
      setCommitments([])
      setDealTeam([])
      setCompanyTeamMembers([])
    }
  }, [currentDeal, companyTeamDataKey, isNew, fundOptions])

  const handleFieldChange = useCallback(
    (field: string, value: any) => {
      const updates: Record<string, any> = {}

      if (field === DealFields.accounts__business_city_code || field === DealFields.accounts__business_city_label) {
        const countryCode = value?.data?.countryCode || value?.countryCode
        const cityLabel = value?.name || value?.label || ''
        const cityCode = typeof value === 'object' ? value?.value || value?.code : value

        if (cityCode !== undefined && cityCode !== null && cityCode !== '') {
          if (typeof value === 'object' && value !== null) {
            updates[DealFields.accounts__business_city_label] = value
          } else {
            const cityObject = {
              value: cityCode,
              name: cityLabel || `City ${cityCode}`,
              label: cityLabel || `City ${cityCode}`,
              data: { countryCode }
            }
            updates[DealFields.accounts__business_city_label] = cityObject
          }
        }

        if (countryCode) {
          updates[DealFields.accounts__business_country_code] = countryCode
          if (countriesData.length > 0) {
            const countryLabel = countriesData.find((country: any) => country.code === countryCode)?.name
            if (countryLabel) {
              updates[DealFields.accounts__business_country_label] = countryLabel
            } else {
              updates[DealFields.accounts__business_country_label] = countryCode
            }
          } else {
            updates[DealFields.accounts__business_country_label] = countryCode
          }
        }
      }

      if (
        field === DealFields.accounts__business_country_code ||
        field === DealFields.accounts__business_country_label
      ) {
        const countryCode = value?.value || value
        const countryLabel = value?.label || value

        updates[DealFields.accounts__business_city_code] = null
        updates[DealFields.accounts__business_city_label] = null

        if (countryCode) {
          updates[DealFields.accounts__business_country_code] = countryCode
        }
        if (countryLabel && countryLabel !== countryCode) {
          updates[DealFields.accounts__business_country_label] = countryLabel
        } else if (countriesData.length > 0 && countryCode) {
          const countryName = countriesData.find((country: any) => country.code === countryCode)?.name
          if (countryName) {
            updates[DealFields.accounts__business_country_label] = countryName
          }
        }
      }

      if (field === DealFields.accounts__office_city_code || field === DealFields.accounts__office_city_code__label) {
        const countryCode = value?.data?.countryCode || value?.countryCode
        const cityLabel = value?.name || value?.label || ''
        const cityCode = typeof value === 'object' ? value?.value || value?.code : value

        if (cityCode !== undefined && cityCode !== null && cityCode !== '') {
          if (typeof value === 'object' && value !== null) {
            updates[DealFields.accounts__office_city_code__label] = value
          } else {
            const cityObject = {
              value: cityCode,
              name: cityLabel || `City ${cityCode}`,
              label: cityLabel || `City ${cityCode}`,
              data: { countryCode }
            }
            updates[DealFields.accounts__office_city_code__label] = cityObject
          }
        }

        if (countryCode) {
          updates[DealFields.accounts__office_country_code] = countryCode
          if (countriesData.length > 0) {
            const countryLabel = countriesData.find((country: any) => country.code === countryCode)?.name
            if (countryLabel) {
              updates[DealFields.accounts__office_country_code__label] = countryLabel
            } else {
              updates[DealFields.accounts__office_country_code__label] = countryCode
            }
          } else {
            updates[DealFields.accounts__office_country_code__label] = countryCode
          }
        }
      }

      if (
        field === DealFields.accounts__office_country_code ||
        field === DealFields.accounts__office_country_code__label
      ) {
        const countryCode = value?.value || value
        const countryLabel = value?.label || value

        updates[DealFields.accounts__office_city_code] = null
        updates[DealFields.accounts__office_city_code__label] = null

        if (countryCode) {
          updates[DealFields.accounts__office_country_code] = countryCode
        }
        if (countryLabel && countryLabel !== countryCode) {
          updates[DealFields.accounts__office_country_code__label] = countryLabel
        } else if (countriesData.length > 0 && countryCode) {
          const countryName = countriesData.find((country: any) => country.code === countryCode)?.name
          if (countryName) {
            updates[DealFields.accounts__office_country_code__label] = countryName
          }
        }
      }

      if (field === 'same_as_business_address') {
        updates[DealFields.accounts__same_as_business_address] = value

        if (value === true) {
          updates[DealFields.accounts__office_address_1] = null
          updates[DealFields.accounts__office_address_2] = null
          updates[DealFields.accounts__office_city_code] = null
          updates[DealFields.accounts__office_stateprovince] = null
          updates[DealFields.accounts__office_postalcode] = null
          updates[DealFields.accounts__office_country_code] = null
        }
      }

      if (field === 'is_funding_round' && value === false) {
        updates[DealFields.rounds__raise_amount] = null
        updates[DealFields.rounds__pre_money_valuation] = null
        updates[DealFields.rounds__post_money_valuation] = null
        updates[DealFields.rounds__name] = null
        updates[DealFields.rounds__funding_stage] = null
        updates[DealFields.rounds__security_type] = null
      }

      if (Object.keys(updates).length > 0 && !isNew && recordId && currentDeal) {
        dispatch(updateDealRecord({ dealId: recordId, updates }))
      }
    },
    [currentDeal, recordId, isNew, dispatch, countriesData]
  )

  const handleCompanyTeamMembersChange = useCallback(
    (updatedCompanyTeamMembers: any[]) => {
      setCompanyTeamMembers(updatedCompanyTeamMembers)

      if (recordId && !isNew && recordId !== 'new') {
        const backendFormatMembers = updatedCompanyTeamMembers.map((member: any) => {
          const contactData = member.contact?.data || {}
          return {
            id: member.employeeRelationshipId || member.key,
            contact_id: member.contact?.value || contactData.id,
            relationship_type: RELATIONSHIP_TYPE_EMPLOYEE,
            founder: member.founder || false,
            title: member.title,
            contacts: {
              id: member.contact?.value || contactData.id,
              email: member.email || contactData.email,
              first_name: contactData.first_name,
              last_name: contactData.last_name,
              mobile_phone: member.contactNumber || contactData.mobile_phone,
              preferred_name: contactData.preferred_name,
              display_name: contactData.display_name || member.contact?.label
            }
          }
        })

        dispatch(
          updateDealRecord({
            dealId: recordId.toString(),
            updates: {
              company_team_members: backendFormatMembers
            }
          })
        )
      }
    },
    [recordId, isNew, dispatch]
  )

  const handleCommitmentsChange = useCallback((updatedCommitments: any[]) => {
    setCommitments(updatedCommitments)
  }, [])

  const shouldShowLoading = !isNew && !currentDeal && isFetchingDeal

  if (shouldShowLoading) {
    return (
      <div className='h-full'>
        <div className='w-full bg-gray-50 p-3'>
          <div className='mx-auto' style={{ maxWidth: 'calc(100vw - 24px)' }}>
            <div className='flex items-center justify-center min-h-[400px]'>
              <Spin size='large' />
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <DndProvider backend={HTML5Backend}>
      <div
        data-debug='full-form-container'
        data-component='FullDealForm'
        data-record-id={recordId}
        data-is-new={isNew}
        data-active-tab={activeTab}
        className='h-full w-full min-h-0 flex flex-col'
      >
        <div data-debug='full-form-wrapper' className='flex-1 w-full h-full bg-gray-50 p-3 overflow-hidden'>
          <div data-debug='full-form-content' className='flex-1 flex-col w-full h-full'>
            <Card
              data-debug='full-form-main-card'
              size='small'
              className='flex-1 min-h-0 flex flex-col h-full'
              classNames={{ body: 'h-full' }}
            >
              <Form
                key={`form-${recordId || 'new'}-${isNew ? 'new' : 'edit'}`}
                data-debug='full-form-form'
                className='h-full'
                form={form}
                layout='vertical'
              >
                <Row data-debug='full-form-main-row' className='h-full' gutter={16}>
                  <Col span={24} className='h-full'>
                    <Tabs
                      key={`tabs-${recordId || 'new'}-${isNew ? 'new' : 'edit'}`}
                      data-debug='full-form-tabs'
                      activeKey={activeTab}
                      onChange={setActiveTab}
                      type='line'
                      size='middle'
                      className='deal-form-tabs'
                      tabBarExtraContent={
                        !isNew ? (
                          <Space size='small'>
                            <AgentProvenanceTag provenance={currentDeal?.[DealFields.prog__created_provenance]} />
                            <RecordProvenanceTag
                              createdByName={currentDeal?.[DealFields.deals__created_by_name]}
                              dateCreated={currentDeal?.[DealFields.deals__date_created]}
                              agentProvenance={currentDeal?.[DealFields.prog__created_provenance]}
                            />
                            <RecordFormDeleteEllipses
                              confirmTitle={CONFIRM_DELETE_DEAL_TITLE}
                              onDelete={handleDeleteDeal}
                              loading={isDeletingDeal}
                              disabled={isFetchingDeal}
                            />
                            <Tooltip title='Refresh'>
                              <Button
                                variant='default'
                                data-debug='refresh-full-form-btn'
                                size='small'
                                icon={<FontAwesomeIcon icon={faArrowsRotate} style={{ color: '#9ca3af' }} />}
                                onClick={handleRefresh}
                                loading={isFetchingDeal}
                                disabled={isDeletingDeal}
                                style={{ width: '32px', height: '32px', padding: 0, minWidth: 0 }}
                              />
                            </Tooltip>
                          </Space>
                        ) : null
                      }
                      items={[
                        {
                          key: dashboardTabKey,
                          label: 'Dashboard',
                          children: (
                            <div data-debug='full-form-dashboard-tab' className='h-full'>
                              <DashboardTab
                                key={dashboardTabKey}
                                dealRecord={currentDeal}
                                handleFieldChange={handleFieldChange}
                                onPatchSuccess={handlePatchSuccess}
                                dealTeam={dealTeam}
                                recordId={recordId}
                                commitments={commitments}
                                handleCommitmentChange={handleCommitmentsChange}
                                getCurrencySymbol={getCurrencySymbol}
                                companyTeamMembers={companyTeamMembers}
                                companyTeamMembersKey={companyTeamMembersKey}
                                onCompanyTeamMembersChange={handleCompanyTeamMembersChange}
                                onRefreshInteractionsReady={(refreshFn: () => Promise<void>) => {
                                  refreshInteractionsRef.current = refreshFn
                                }}
                                onRefreshTasksReady={(refreshFn: () => Promise<void>) => {
                                  refreshTasksRef.current = refreshFn
                                }}
                                showAgentPanels={hasDealFollower}
                                onNavigateToAgentTab={() => setActiveTab(agentTabKey)}
                                onRefreshAgentReady={(refreshFn: () => Promise<void>) => {
                                  refreshAgentSignalsRef.current = refreshFn
                                }}
                                onRefreshDealStatesReady={(refreshFn: () => Promise<void>) => {
                                  refreshDealStatesRef.current = refreshFn
                                }}
                              />
                            </div>
                          )
                        },
                        {
                          key: detailTabKey,
                          label: 'Detail',
                          children: (
                            <div data-debug='full-form-detail-tab' className='h-full'>
                              <DetailTab
                                key={detailTabKey}
                                dealRecord={currentDeal}
                                handleFieldChange={handleFieldChange}
                                onPatchSuccess={handlePatchSuccess}
                                dealTeam={dealTeam}
                                recordId={recordId}
                                commitments={commitments}
                                handleCommitmentChange={handleCommitmentsChange}
                                companyTeamMembers={companyTeamMembers}
                                companyTeamMembersKey={companyTeamMembersKey}
                                onCompanyTeamMembersChange={handleCompanyTeamMembersChange}
                              />
                            </div>
                          )
                        },
                        ...(!isNew && recordId
                          ? [
                              {
                                key: executionTabKey,
                                label: 'Execution',
                                children: (
                                  <div data-debug='full-form-execution-tab' className='h-full overflow-y-auto'>
                                    <ExecutionTab
                                      key={executionTabKey}
                                      dealId={recordId}
                                      onNavigateToAgentTab={() => setActiveTab(agentTabKey)}
                                      defaultAssignees={dealLeadAssignees}
                                      onTaskCreated={handleRefreshTasksPanel}
                                      onRefreshAgentReady={(refreshFn: () => Promise<void>) => {
                                        refreshExecutionAgentRef.current = refreshFn
                                      }}
                                      onRefreshReady={(refreshFn: () => Promise<void>) => {
                                        refreshExecutionDataRef.current = refreshFn
                                      }}
                                    />
                                  </div>
                                )
                              },
                              {
                                key: wireTabKey,
                                label: 'Wire',
                                children: (
                                  <div data-debug='full-form-wire-tab' className='h-full overflow-y-auto'>
                                    <WireTab
                                      key={wireTabKey}
                                      dealId={recordId}
                                      companyId={(currentDeal?.[DealFields.accounts__id] as string) ?? null}
                                      onRefreshReady={(refreshFn: () => Promise<void>) => {
                                        refreshWireRef.current = refreshFn
                                      }}
                                    />
                                  </div>
                                )
                              },
                              {
                                key: documentsTabKey,
                                label: 'Documents',
                                children: (
                                  <div data-debug='full-form-documents-tab' className='h-full overflow-y-auto'>
                                    <DocumentsTab
                                      key={documentsTabKey}
                                      dealId={recordId}
                                      onRefreshReady={(refreshFn: () => Promise<void>) => {
                                        refreshDocumentsRef.current = refreshFn
                                      }}
                                    />
                                  </div>
                                )
                              }
                            ]
                          : []),
                        // Agent tab (deal follower, placed after Documents —
                        // the company-form Agent tab's sibling): visible while
                        // the deal is (or has been) in the Origination or
                        // Execution pipeline. The denormalized pipeline
                        // pointers persist after the deal leaves, keeping the
                        // follower's history reachable through the unfollow
                        // lag and after the sweep pauses it.
                        ...(hasDealFollower && recordId
                          ? [
                              {
                                key: agentTabKey,
                                label: 'Agent',
                                children: (
                                  <div data-debug='full-form-agent-tab' className='h-full overflow-y-auto'>
                                    <AgentTab
                                      key={agentTabKey}
                                      subjectTable='deals'
                                      subjectId={recordId}
                                      onRefreshReady={(refreshFn) => {
                                        refreshAgentTabRef.current = refreshFn
                                      }}
                                    />
                                  </div>
                                )
                              }
                            ]
                          : []),
                        ...(!isNew && recordId
                          ? [
                              {
                                key: changeLogTabKey,
                                label: 'Change Log',
                                children: (
                                  <div data-debug='full-form-change-log-tab' className='h-full'>
                                    <ChangeLogTab
                                      key={changeLogTabKey}
                                      contextTable='deals'
                                      recordId={recordId}
                                    />
                                  </div>
                                )
                              }
                            ]
                          : [])
                      ]}
                    />
                  </Col>
                </Row>
              </Form>
            </Card>
          </div>
        </div>
      </div>
    </DndProvider>
  )
}
